/**
 * Готовность данных к планированию сезона — слой над `GET /planning/readiness`
 * (UC-0 / FR-0.1–0.4, AK-763; backend `services/planning_readiness.py`).
 *
 * Экран ничего не считает сам — и это не лень, а требование FR-0.1: чек-лист
 * ВЫЧИСЛЯЕТСЯ системой из данных. Как только фронт начнёт «дорисовывать»
 * статус (например, красить пункт по своему порогу), появятся два источника
 * правды и вердикт блока разъедется с цветом строк. Поэтому здесь только типы,
 * запрос и подача чисел человеку.
 *
 * Wireframe (одобрен founder 26.08):
 * `docs/superpowers/prototypes/season-planning-2027/01-readiness.html`.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

/** Светофор пункта и блока. Словарь ЗАДАЁТ бэкенд — фронт его не расширяет. */
export type ReadinessStatus = 'ok' | 'warn' | 'missing';

export interface ReadinessItem {
  key: string;
  label: string;
  status: ReadinessStatus;
  /** Готовая фраза «211 / 315 позиций с ценой на 2027» — на языке экономиста. */
  value_text: string;
  /** Числитель/знаменатель для полосы. `null` — пункт булев, полосы нет. */
  value_current: number | null;
  value_total: number | null;
  /** FR-0.2 — дата свежести источника (ISO). */
  freshness_date: string | null;
  /** FR-0.2 — откуда цифра. */
  source: string | null;
  /** FR-0.3 — экран с уже наложенным фильтром. `null` — экрана ещё нет. */
  fix_url: string | null;
  fix_label: string | null;
  /** Пояснение вместо ссылки (или под ней), когда чинить надо не в интерфейсе. */
  fix_hint: string | null;
}

export interface ReadinessBlock {
  key: 'minimal' | 'sufficient';
  title: string;
  hint: string;
  status: ReadinessStatus;
  verdict_title: string;
  verdict_summary: string;
  items: ReadinessItem[];
}

export interface Readiness {
  farm_id: string;
  farm_name: string;
  season_id: string;
  season_year: number;
  variant_id: string | null;
  variant_name: string | null;
  fields_total: number;
  accounting_area_ha: number;
  computed_at: string;
  blocks: ReadinessBlock[];
}

export const readinessKeys = {
  all: () => ['planning', 'readiness'] as const,
  farm: (farmId: string | null, seasonId: string | null) =>
    ['planning', 'readiness', farmId ?? 'none', seasonId ?? 'none'] as const,
};

export function getReadiness(farmId: string, seasonId: string): Promise<Readiness> {
  const qs = new URLSearchParams({ farm_id: farmId, season_id: seasonId });
  return api.get<Readiness>(`/api/v1/planning/readiness?${qs.toString()}`);
}

export function useReadiness(
  farmId: string | null,
  seasonId: string | null,
): UseQueryResult<Readiness> {
  return useQuery({
    queryKey: readinessKeys.farm(farmId, seasonId),
    queryFn: () => getReadiness(farmId as string, seasonId as string),
    enabled: !!farmId && !!seasonId,
    // Чек-лист — снимок состояния данных, а не живой поток: человек уходит
    // чинить в соседнюю вкладку и возвращается. Дальше он жмёт «Пересчитать»
    // (refetch) — кнопка на экране есть намеренно, чтобы «пересчитано в 09:14»
    // не было враньём.
    staleTime: 30_000,
  });
}

/**
 * Ширина полосы заполненности, %.
 *
 * Пустой пункт рисуется НЕ нулём, а тонкой засечкой: полоса нулевой ширины
 * визуально исчезает, и строка «0 из 98» выглядит так же, как строка без
 * полосы вообще (то есть как булев пункт). Засечка сохраняет разницу между
 * «нечего показывать» и «показывать нечего, потому что пусто».
 */
export function barWidthPct(item: ReadinessItem): number | null {
  if (item.value_total === null || item.value_total <= 0) return null;
  const current = item.value_current ?? 0;
  if (current <= 0) return 2;
  return Math.min(100, Math.round((current / item.value_total) * 100));
}

/**
 * Разбор серверной ссылки «где чинить» на то, что понимает `<Link>`.
 *
 * Адрес приходит СТРОКОЙ с сервера (`/fields?farm_id=…`) — вместе с пунктом,
 * потому что состав пунктов там же. Роутер типизирован и хочет `to` отдельно
 * от `search`, а строку с `?` разобрал бы не так, как мы ждём. Разбор — чистая
 * функция ровно затем, чтобы «ссылка ведёт на экран С ФИЛЬТРОМ» (FR-0.3)
 * проверялось тестом, а не глазами.
 *
 * Внешний адрес (другой origin) в ответе не предусмотрен; если он всё-таки
 * появится, отдаём `null` — молча увести человека с приложения по данным из
 * API нельзя.
 */
export function fixLinkTarget(
  url: string | null,
): { to: string; search: Record<string, string> } | null {
  if (!url || !url.startsWith('/')) return null;
  const [path, query] = url.split('?', 2);
  const search: Record<string, string> = {};
  if (query) {
    for (const [k, v] of new URLSearchParams(query).entries()) search[k] = v;
  }
  return { to: path, search };
}
